/**
 * Admin write schemas for WebsitePage (sub-project 2 - CMS write API).
 *
 * These shapes back POST/PATCH/DELETE/PUBLISH/UNPUBLISH on
 * /api/v1/admin/website/pages. The PUBLIC read schemas live in
 * website-sections.ts and are not affected.
 *
 * Naming intentionally mirrors leads.ts / bookings.ts / customers.ts:
 * <Verb><Resource>RequestSchema (input) + <Resource>DetailSchema (output).
 */

import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

// Mirror prisma enum WebsitePageType.
export const WebsitePageTypeSchema = z.enum([
  'STATIC',
  'DESTINATION_LANDING',
  'TOURISM_BOARD',
  'MICE',
  'PACKAGE_CATEGORY',
  'PACKAGE_TAG',
  'CUSTOM',
  'LANDING_PAGE',
  'BEST_TIME_TO_VISIT',
  'BEST_PLACES_TO_VISIT',
]);
export type WebsitePageType = z.infer<typeof WebsitePageTypeSchema>;

/**
 * Page types that get their own sidebar item, permission module and list
 * screen instead of living in the generic Custom Pages list. The API resolves
 * a request's permission module from the row's pageType via this map, so a
 * Tourism Board editor cannot reach a Landing Page (or a Custom Page) even
 * though all three are WebsitePage rows behind the same endpoints.
 *
 * A type absent from this map falls back to `web.pages` - the Custom Pages
 * module - which is the pre-existing behaviour for STATIC/CUSTOM/etc.
 */
export const WEBSITE_PAGE_TYPE_PERMISSION_MODULES = {
  TOURISM_BOARD: 'web.tourism_boards',
  LANDING_PAGE: 'web.landing_pages',
  // 2026-08-03. The two destination SEO guides. Separate modules (not one
  // shared "guides" module) so an SEO writer can be given "Best places" only.
  BEST_TIME_TO_VISIT: 'web.best_time_pages',
  BEST_PLACES_TO_VISIT: 'web.best_places_pages',
} as const satisfies Partial<Record<WebsitePageType, string>>;

/** Permission module that governs a page of this type. */
export const permissionModuleForPageType = (pageType: WebsitePageType): string =>
  (WEBSITE_PAGE_TYPE_PERMISSION_MODULES as Partial<Record<WebsitePageType, string>>)[pageType] ??
  'web.pages';

// Mirror prisma enum WebsitePublishStatus.
export const WebsitePublishStatusSchema = z.enum([
  'DRAFT',
  'PENDING_REVIEW',
  'PUBLISHED',
  'ARCHIVED',
]);
export type WebsitePublishStatus = z.infer<typeof WebsitePublishStatusSchema>;

// kebab-case slugs only. Lowercase letters, digits, dashes, slashes (for
// nested paths like packages/europe-tours).
const SlugSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9-/]+$/u, 'Slug must be lowercase letters, digits, dashes, or slashes');

export const CreateWebsitePageRequestSchema = z.object({
  pageType: WebsitePageTypeSchema,
  slug: SlugSchema,
  title: z.string().min(1).max(255),
  metaTitle: z.string().max(255).nullish(),
  metaDescription: z.string().max(500).nullish(),
  metaKeywords: z.string().max(500).nullish(),
  ogImageId: UuidSchema.nullish(),
  heroMediaId: UuidSchema.nullish(),
  destinationId: UuidSchema.nullish(),
  showInSitemap: z.boolean().default(true),
  noIndex: z.boolean().default(false),
});
export type CreateWebsitePageRequest = z.infer<typeof CreateWebsitePageRequestSchema>;

// expectedUpdatedAt is the optimistic-concurrency guard. Client passes the
// updatedAt it last saw; if the row has moved since, the service returns 409.
export const UpdateWebsitePageRequestSchema = CreateWebsitePageRequestSchema.partial().extend({
  expectedUpdatedAt: IsoDateTimeSchema.optional(),
});
export type UpdateWebsitePageRequest = z.infer<typeof UpdateWebsitePageRequestSchema>;

export const WebsitePageDetailSchema = z.object({
  id: UuidSchema,
  pageType: WebsitePageTypeSchema,
  slug: z.string(),
  title: z.string(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  ogImageId: UuidSchema.nullable(),
  heroMediaId: UuidSchema.nullable(),
  destinationId: UuidSchema.nullable(),
  status: WebsitePublishStatusSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  showInSitemap: z.boolean(),
  noIndex: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type WebsitePageDetail = z.infer<typeof WebsitePageDetailSchema>;
